import { useDebounce } from 'ahooks';
import axios from 'axios';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tAdvanceSearchLead } from '../../../AdvanceSearch/AdvanceSearchModal.types';
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_PAGE_SIZE,
} from '../../../../pages/MessengerV2/MessageSearch.constants';
import {
  defaultLeadSearchSince,
  normalizeSearchTerm,
} from '../../../../pages/MessengerV2/MessageSearch.functions';
import { MessageSearchService } from '../../../../pages/MessengerV2/MessageSearch.service';
import {
  tAnsweredSearch,
  tLeadMessageSearchResult,
  tSearchLeadMessagesQueryParams,
} from '../../../../pages/MessengerV2/MessageSearch.types';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context';
import { tFocusedChatEvent } from '../../../../providers/ModalsProvider/Modals.types';
import { eChannelType } from '../../../../providers/SocketsProvider/Sockets.enums';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context';
import { useChatContext } from '../../Chat.hook';
import {
  canSearchTerm,
  deriveChatSearchStatus,
  extractSnippetTerms,
  readPagingState,
  resolveOlderStep,
} from './ChatSearch.functions';
import {
  tChatSearchContext,
  tChatSearchMotion,
  tChatSearchMotionOptions,
} from './ChatSearch.types';

/** How long "Esta coincidencia es de Instagram" stays up after a switch. */
export const CHANNEL_NOTICE_MS = 4200;

type tSearchOrigin = {
  leadId: string;
  integration: eChannelType | undefined;
};

/**
 * "Buscar en esta conversación" — search one contact's messages, across all of
 * its channels, and walk the matches in the thread itself (SRE-3328,
 * prototype A).
 *
 * One instance per chat panel (ChatProvider): the Messenger page and the Pipe
 * and Directory chat modals each get their own, and none of them depends on
 * the inbox's MessageSearchProvider, which exists only on the Messenger page.
 *
 * The concurrency discipline is the inbox search's, and for the same reasons:
 * the request id stops a response that outlived its search from writing state,
 * the abort stops the server work behind it, `since` is frozen for a cursor
 * chain so a later page cannot see a different window, and a cancellation is
 * this hook's own control flow, never an error.
 *
 * Stepping to a match reuses the inbox jump (`focusedChatEvent` → `?around=`)
 * rather than anything of its own, adding only the channel: a match on another
 * channel switches the chat the way the channel dropdown does, and names its
 * chat so the jump waits for it.
 */
export default function useChatSearch(): tChatSearchContext {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { selectedChat, returnToLatest } = useChatContext();
  const {
    selectedLeadForChatModal,
    setSelectedLeadForChatModal,
    setFocusedChatEvent,
  } = useModalsContext();

  const leadId = selectedLeadForChatModal?.id ?? '';
  const requestedIntegration =
    selectedLeadForChatModal?.chat?.integration ?? selectedChat?.integration;

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [matches, setMatches] = useState<tLeadMessageSearchResult[]>([]);
  const [answered, setAnswered] = useState<tAnsweredSearch | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [partial, setPartial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [lastPageEmpty, setLastPageEmpty] = useState(false);
  const [channelNotice, setChannelNotice] = useState<eChannelType | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  // Presentation only (the bar's enter and exit); nothing below reads it.
  const [motion, setMotion] = useState<tChatSearchMotion>('instant');

  const debouncedInput = useDebounce(inputValue, { wait: SEARCH_DEBOUNCE_MS });
  const typedTerm = normalizeSearchTerm(inputValue);
  const searchTerm = normalizeSearchTerm(debouncedInput);

  const inputRef = useRef<HTMLInputElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const sinceRef = useRef(defaultLeadSearchSince());
  const loadingMoreRef = useRef(false);
  const originRef = useRef<tSearchOrigin | null>(null);
  // Whether any step moved the thread. Closing without one leaves the thread
  // where it is, so nothing is refetched for a search that never jumped.
  const jumpedRef = useRef(false);
  // The last jump this hook asked for, so closing clears only its own pending
  // target and never one an inbox result row just set.
  const lastJumpRef = useRef<tFocusedChatEvent | null>(null);

  // Read inside async continuations, where the render's values are stale.
  const liveRef = useRef({
    matches,
    chatId: selectedChat?.id,
    integration: requestedIntegration,
    leadId,
  });
  liveRef.current = {
    matches,
    chatId: selectedChat?.id,
    integration: requestedIntegration,
    leadId,
  };

  const buildQueryParams = useCallback((nextCursor?: string): tSearchLeadMessagesQueryParams => {
    return {
      query: searchTerm,
      since: sinceRef.current,
      limit: SEARCH_PAGE_SIZE,
      ...(nextCursor && { cursor: nextCursor }),
    };
  }, [searchTerm]);

  /**
   * Moves the thread to one match: switches channel first when the match lives
   * on another one, then asks for the jump.
   */
  const goTo = useCallback((index: number, list: tLeadMessageSearchResult[]) => {
    const match = list[index];

    if (!match) return;

    const live = liveRef.current;
    const onScreen = match.chatId === live.chatId;
    // Only a different channel can be switched to. A match on another chat of
    // the SAME channel cannot be reached from here; it goes out without its
    // chatId so the jump degrades with the usual notice instead of waiting for
    // a chat that will never load.
    const switchesChannel =
      !onScreen && Boolean(match.channel) && match.channel !== live.integration;

    setActiveIndex(index);
    jumpedRef.current = true;

    if (switchesChannel) {
      setSelectedLeadForChatModal((previous) =>
        previous
          ? ({
              ...previous,
              chat: { ...previous.chat, integration: match.channel },
            } as tAdvanceSearchLead)
          : previous,
      );
    }

    setChannelNotice(switchesChannel ? match.channel : null);

    const target: tFocusedChatEvent = {
      leadId: match.leadId || live.leadId,
      eventId: match.eventId,
      ...((onScreen || switchesChannel) && { chatId: match.chatId }),
    };

    lastJumpRef.current = target;
    setFocusedChatEvent(target);
  }, [setFocusedChatEvent, setSelectedLeadForChatModal]);

  const resetResults = useCallback(() => {
    loadingMoreRef.current = false;
    setMatches([]);
    setAnswered(null);
    setActiveIndex(-1);
    setCursor('');
    setHasMore(false);
    setPartial(false);
    setLoadingMore(false);
    setFailed(false);
    setLoadMoreFailed(false);
    setLastPageEmpty(false);
    setChannelNotice(null);
  }, []);

  /** Tears the search down. Restoring the thread is close()'s job, not this. */
  const shutDown = useCallback(() => {
    requestIdRef.current += 1;
    abortRef.current?.abort();
    setIsOpen(false);
    setInputValue('');
    setLoading(false);
    resetResults();
    // A jump still waiting for its channel to load belongs to a search that no
    // longer exists. Only ours: an inbox row may have set one since.
    setFocusedChatEvent((pending) =>
      pending && pending === lastJumpRef.current ? null : pending,
    );
    lastJumpRef.current = null;
    originRef.current = null;
    jumpedRef.current = false;
    // Closed by something other than "Cerrar" (another contact opened): the
    // bar goes at once.
    setMotion('instant');
  }, [resetResults, setFocusedChatEvent]);

  // One search per settled text. Everything is reset first, so a result of the
  // previous text can never be stepped through under the new one.
  useEffect(() => {
    const requestId = ++requestIdRef.current;

    abortRef.current?.abort();
    resetResults();

    if (
      !isOpen ||
      !currentWorkspaceId ||
      !leadId ||
      !canSearchTerm(searchTerm)
    ) {
      // Under the minimum, or exclusions only: no request at all. bff-web would
      // answer an empty 200 that reads exactly like "no matches".
      setLoading(false);

      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    sinceRef.current = defaultLeadSearchSince();
    setLoading(true);

    (async () => {
      try {
        const response = await MessageSearchService.searchLeadMessages({
          workspaceId: currentWorkspaceId,
          leadId,
          queryParams: buildQueryParams(),
          signal: controller.signal,
        });

        if (requestId !== requestIdRef.current) return;

        const page = response.data ?? {};
        const rows = uniqBy(page.messages ?? [], 'eventId');
        const paging = readPagingState(page);

        setMatches(rows);
        setAnswered({ term: searchTerm, firstPageCount: rows.length });
        setCursor(paging.cursor);
        setHasMore(paging.hasMore);
        setPartial(paging.partial);

        // The newest match is focused on its own, as WhatsApp does: the search
        // answers "where is it" by going there.
        if (rows.length) goTo(0, rows);
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (requestId !== requestIdRef.current) return;

        setFailed(true);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    })();

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18 preserves the existing query keys and cancellation boundaries.
  }, [isOpen, searchTerm, leadId, currentWorkspaceId, retryToken]);

  // Another contact opened under the bar: the search belonged to the previous
  // one. Closed without restoring anything — the new chat loads at its newest.
  useEffect(() => {
    if (!isOpen || !originRef.current) return;
    if (originRef.current.leadId === leadId) return;

    shutDown();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18 keeps contact-change teardown keyed to the lead id.
  }, [leadId]);

  useEffect(() => {
    if (!channelNotice) return;

    const timer = setTimeout(() => setChannelNotice(null), CHANNEL_NOTICE_MS);

    return () => clearTimeout(timer);
  }, [channelNotice, activeIndex]);

  // The provider unmounts with the chat panel (a modal closing, a page leaving).
  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !cursor || !currentWorkspaceId || !leadId)
      return;

    const requestId = requestIdRef.current;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLastPageEmpty(false);
    setLoadMoreFailed(false);

    try {
      // Shares the search's controller: a new text supersedes a page fetch just
      // as much as it supersedes a search.
      const response = await MessageSearchService.searchLeadMessages({
        workspaceId: currentWorkspaceId,
        leadId,
        queryParams: buildQueryParams(cursor),
        signal: abortRef.current?.signal,
      });

      if (requestId !== requestIdRef.current) return;

      const page = response.data ?? {};
      const before = liveRef.current.matches;
      const merged = uniqBy([...before, ...(page.messages ?? [])], 'eventId');
      const paging = readPagingState(page);

      setMatches(merged);
      setCursor(paging.cursor);
      setHasMore(paging.hasMore);
      setPartial(paging.partial);

      // The index never restarts: the first new match is the next older one.
      if (merged.length > before.length) goTo(before.length, merged);
      else setLastPageEmpty(true);
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (requestId !== requestIdRef.current) return;

      setLoadMoreFailed(true);
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [buildQueryParams, currentWorkspaceId, cursor, goTo, leadId]);

  const open = useCallback((options?: tChatSearchMotionOptions) => {
    if (isOpen) {
      inputRef.current?.focus();
      inputRef.current?.select();

      return;
    }

    originRef.current = { leadId, integration: requestedIntegration };
    jumpedRef.current = false;
    setMotion(options?.animate ? 'animate' : 'instant');
    setIsOpen(true);
  }, [isOpen, leadId, requestedIntegration]);

  /**
   * Esc / "Cerrar": closes the bar, drops the marks and puts the thread back on
   * the newest message of the channel the search started on.
   */
  const close = useCallback((options?: tChatSearchMotionOptions) => {
    if (!isOpen) return;

    const origin = originRef.current;
    const jumped = jumpedRef.current;

    shutDown();
    setMotion(options?.animate ? 'animate' : 'instant');

    if (origin && origin.leadId === liveRef.current.leadId) {
      if (
        origin.integration &&
        origin.integration !== liveRef.current.integration
      ) {
        // Back to the starting channel: loading it lands on its newest page.
        setSelectedLeadForChatModal((previous) =>
          previous
            ? ({
                ...previous,
                chat: { ...previous.chat, integration: origin.integration },
              } as tAdvanceSearchLead)
            : previous,
        );
      } else if (jumped) {
        void returnToLatest();
      }
    }

    setTimeout(() => openButtonRef.current?.focus(), 0);
  }, [isOpen, returnToLatest, setSelectedLeadForChatModal, shutDown]);

  const older = useCallback(() => {
    const step = resolveOlderStep({
      activeIndex,
      matchCount: matches.length,
      hasMore,
      busy: loading || loadingMoreRef.current,
    });

    if (step.kind === 'goto') goTo(step.index, matches);
    else if (step.kind === 'fetch') void loadMore();
  }, [activeIndex, goTo, hasMore, loadMore, loading, matches]);

  const newer = useCallback(() => {
    if (loading || activeIndex <= 0) return;

    goTo(activeIndex - 1, matches);
  }, [activeIndex, goTo, loading, matches]);

  const continueSearch = useCallback(() => {
    if (!hasMore) return;

    void loadMore();
  }, [hasMore, loadMore]);

  const retry = useCallback(() => {
    if (matches.length) {
      continueSearch();

      return;
    }

    setRetryToken((token) => token + 1);
  }, [continueSearch, matches.length]);

  const searchTerms = useMemo(
    () => extractSnippetTerms(matches.map((match) => match.snippet)),
    [matches],
  );

  return useMemo(() => ({
    isOpen,
    open,
    close,
    motion,
    inputValue,
    setInputValue,
    status: deriveChatSearchStatus({
      isOpen,
      typedTerm,
      searchTerm,
      loading,
      failed,
      matchCount: matches.length,
    }),
    matches,
    answered,
    activeIndex,
    hasMore,
    partial,
    loadingMore,
    lastPageEmpty,
    loadMoreFailed,
    channelNotice,
    searchTerms,
    older,
    newer,
    continueSearch,
    retry,
    inputRef,
    openButtonRef,
  }), [
    activeIndex,
    answered,
    channelNotice,
    close,
    continueSearch,
    failed,
    hasMore,
    inputValue,
    isOpen,
    lastPageEmpty,
    loadMoreFailed,
    loading,
    loadingMore,
    matches,
    motion,
    newer,
    older,
    open,
    partial,
    retry,
    searchTerm,
    searchTerms,
    typedTerm,
  ]);
}
